import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Row, Col, Card, CardBody, Spinner } from 'reactstrap'
import moment from 'moment'
import { getRegs, getReceitasFromOikos } from '../../utils/fc'
import currencyFormatter from '../../utils/currencyFormatter'
import { parseDate, startOfToday, isFaturado } from '../Financial/Receitas/ContasAReceber/utils'

const DAYS = 30
const CUTOFF = parseDate('2025-01-01')

const PROD_FIELDS = {
  '(SELECT proj.proj_nome FROM proj WHERE proj.proj_id = prod.proj_id)': 'proj_nome',
  '(SELECT proj.projSituacao_id FROM proj WHERE proj.proj_id = prod.proj_id AND proj.proj_ativo = 1)': 'projSituacao_id',
  '(SELECT psa.prodStatusAlt_data FROM prodStatusAlt psa WHERE psa.prod_id = prod.prod_id AND psa.prodStatusTp_id = 8 AND psa.prodStatusAlt_ativo = 1 ORDER BY prodStatusAlt_dtCria ASC LIMIT 1)': 'prodAutFatData',
}

const startOfTomorrowPlus = (today, n) => {
  const limit = new Date(today)
  limit.setDate(today.getDate() + n)
  return limit
}

const fmtDate = (d) => (d ? moment(d).format('DD/MM/YYYY') : '-')

const recBillingDate = (rec) =>
  parseDate(rec?.rec_dtNfAtual || rec?.rec_dtNFAtual || rec?.rec_dtNfPrev)

const isProdutoNaoFaturado = (p) => !p?.prod_dtAutFat && !p?.prodAutFatData

/** De 01/01/2025 até (hoje + n dias), incluindo atrasados a partir do corte. */
const inWindowIncludingOverdue = (d, today, n = DAYS) => {
  if (!d) return false
  return d >= CUTOFF && d < startOfTomorrowPlus(today, n)
}

const dueColor = (d, today) => {
  if (!d) return undefined
  if (d < today) return 'red'
  const warn = new Date(d)
  warn.setDate(d.getDate() - 4)
  if (today >= warn) return '#fcb92c'
  return undefined
}

const sortByDate = (a, b, getDate) => {
  const da = getDate(a)?.getTime() ?? 0
  const db = getDate(b)?.getTime() ?? 0
  return da - db
}

const ListCard = ({ title, to, count, extra, loading, emptyText, children }) => (
  <Col md={6} className="mb-3">
    <Card>
      <CardBody style={{ overflow: 'auto', maxHeight: 360 }}>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="card-title mb-1">
              {to ? <Link to={to}>{title}</Link> : title}
            </h4>
            {extra ? <div style={{ fontSize: 12, color: '#6c757d' }}>{extra}</div> : null}
          </div>
          <span style={{ fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{loading ? '—' : count}</span>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-4">
            <Spinner />
          </div>
        ) : count === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>{emptyText}</div>
        ) : (
          children
        )}
      </CardBody>
    </Card>
  </Col>
)

const ItemBlock = ({ color, children }) => (
  <div style={{ color, marginBottom: 16, fontSize: 13 }}>
    {children}
  </div>
)

const FaturamentoProximosCards = () => {
  const [loading, setLoading] = useState(true)
  const [produtos, setProdutos] = useState([])
  const [recs, setRecs] = useState([])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      try {
        const today = startOfToday()
        const [prodRaw, recRaw] = await Promise.all([
          getRegs('prod', {}, PROD_FIELDS),
          getReceitasFromOikos(),
        ])
        if (cancelled) return

        const prods = (Array.isArray(prodRaw) ? prodRaw : [])
          .filter((p) => Number(p.projSituacao_id) === 2)
          .filter(isProdutoNaoFaturado)
          .filter((p) => inWindowIncludingOverdue(parseDate(p.prod_dtLimAprov), today))
          .sort((a, b) => sortByDate(a, b, (x) => parseDate(x.prod_dtLimAprov)))

        const receitas = (Array.isArray(recRaw) ? recRaw : [])
          .filter((r) => !isFaturado(r))
          .filter((r) => inWindowIncludingOverdue(recBillingDate(r), today))
          .sort((a, b) => sortByDate(a, b, recBillingDate))

        setProdutos(prods)
        setRecs(receitas)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  const today = startOfToday()
  const valorRecs = recs.reduce((acc, r) => acc + (Number(r.rec_vlrBru) || 0), 0)
  const atrasadas = recs.filter((r) => {
    const d = recBillingDate(r)
    return d && d < today
  }).length

  return (
    <Row>
      <ListCard
        title="Produtos a faturar (30 dias)"
        to="/dep-tecnico/produtos"
        count={produtos.length}
        extra="Não autorizados, incluindo atrasados"
        loading={loading}
        emptyText="Nenhum produto pendente de faturamento neste período."
      >
        {produtos.map((p) => {
          const d = parseDate(p.prod_dtLimAprov)
          return (
            <ItemBlock key={p.prod_id} color={dueColor(d, today)}>
              <span style={{ fontWeight: 'bold' }}>Projeto: </span>
              <span>{p.proj_nome || '-'}</span>
              <br />
              <span style={{ fontWeight: 'bold' }}>Produto: </span>
              <span>{p.prod_desc || '-'}</span>
              <br />
              <span style={{ fontWeight: 'bold' }}>Limite fat.: </span>
              <span>{fmtDate(d)}</span>
              {d && d < today ? <span> · atrasado</span> : null}
            </ItemBlock>
          )
        })}
      </ListCard>

      <ListCard
        title="Receitas a faturar (30 dias)"
        to="/receitas"
        count={recs.length}
        extra={`${currencyFormatter.format(valorRecs)}${atrasadas ? ` · ${atrasadas} atrasada(s)` : ''}`}
        loading={loading}
        emptyText="Nenhuma receita pendente de faturamento neste período."
      >
        {recs.map((r) => {
          const d = recBillingDate(r)
          return (
            <ItemBlock key={r.rec_id} color={dueColor(d, today)}>
              <span style={{ fontWeight: 'bold' }}>Projeto: </span>
              <span>{r.recProjeto || '-'}</span>
              <br />
              <span style={{ fontWeight: 'bold' }}>EP: </span>
              <span>{r.rec_ep || '-'}</span>
              <br />
              <span style={{ fontWeight: 'bold' }}>Valor: </span>
              <span>{currencyFormatter.format(Number(r.rec_vlrBru) || 0)}</span>
              <br />
              <span style={{ fontWeight: 'bold' }}>Limite fat.: </span>
              <span>{fmtDate(d)}</span>
              {d && d < today ? <span> · atrasada</span> : null}
            </ItemBlock>
          )
        })}
      </ListCard>
    </Row>
  )
}

export default FaturamentoProximosCards
